/* eslint-disable import/first, import/no-named-as-default, import/no-named-default */
import React, { PureComponent, Children } from 'react';
import styled from 'styled-components';
import { Route, Switch, Link, Redirect } from 'react-router-dom';
import {
  Banner,
  App,
  AppFooter,
  AppFooterLink,
  Navigation,
  NavigationList,
  NavigationListItem,
  Type,
} from '@spotify-internal/encore-creator-web';
import tokens from '@spotify-internal/encore-foundation';
import { connect } from 'react-redux';
import { Episode } from '../Episode';
import { Podcast } from '../Podcast';
import { ConnectedNetwork } from '../Network';
import { LoggedInHelpPage } from '../HelpPage/LoggedInHelpPage/index';
import { ConnectedNetworkList } from '../Network/NetworkList';
import { ErrorPage } from '../ErrorPage';
import { ConnectedNetworkLandingPage } from '../Network/NetworkLandingPage';
import { ConnectedNetworkAudience } from '../Network/NetworkAudience';
import { ConnectedPodcastList } from '../Podcast/PodcastList';
import { ConnectedAddPodcastLandingPage } from '../AddPodcast';
import { ProtectedNetworkComponent } from './ProtectedNetworkComponent';
import styles from './index.module.scss';
import { trackEvent } from '../../lib/Tracking';
import { ERROR, UNAUTHORIZED, selectMeStatus, selectCurrentNetworkId } from '../../store/selectors';
import { setCurrentNetworkId } from '../../store/currentNetwork';
import { loadMe } from '../../store/me';
import { formatDate } from '../../lib/Formatter';
import { isS4PodShowSiteAlertEnabled, isS4PodHomeViewForAllEnabled } from '../../lib/Features';
import QueryString from 'query-string';
import { AddSoundtrapPodcast } from '../AddSoundtrapPodcast/';
import { Settings } from '../Settings/';
import { Me, ReduxState, UserType } from '../../types';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';
import { Home } from '../Home/home';
import { getUserType } from '../../lib/User';
import { Layout } from './Layout/index';
import { MeContext } from '../../context/me';
import { FormattedMessage, injectIntl, defineMessages, IntlShape } from 'react-intl';

const StyledAlert = styled(Banner)`
  margin: -48px -32px 32px;
`;

const DataUpdated = styled(Type)`
  padding: 1em 0 0 ${tokens.spacer24};

  @media (max-width: 480px) {
    display: none;
  }
`;

interface Props {
  status: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  currentNetworkId?: string;
  setCurrentNetworkId(id: string): void;
  loadMe(): void;
  intl: IntlShape;
}

export class Root extends PureComponent<Props> {
  static contextType = MeContext;

  defaultNetworkUrl = '/networks';
  logoutUrl = `https://accounts.spotify.com/logout?continue=${window.location.origin}`;

  handleLogoutClick = /* istanbul ignore next */ () => {
    trackEvent({ category: 'user', action: 'logout' });
  };

  renderAlertBannerIfNecessary() {
    if (isS4PodShowSiteAlertEnabled(this.context.features)) {
      return (
        <Banner variant={Banner.warning} data-qa="data-alert">
          <FormattedMessage
            id="home.data-alert"
            description="Message to tell users that there has been a delay processing their data"
            defaultMessage="Data delay: we’ll have this fixed as soon as possible. Thanks for your patience."
          />
        </Banner>
      );
    }
    return null;
  }

  render() {
    switch (this.props.status) {
      case UNAUTHORIZED:
        return (
          <Switch>
            <Route
              path="/submit"
              render={() => {
                window.location.assign(
                  `https://accounts.spotify.com/login?continue=${encodeURIComponent(
                    window.location.href,
                  )}`,
                );

                return <div data-testid="redirect-to-auth" />;
              }}
            />
            <Route
              render={() => (
                <Redirect
                  to={{
                    pathname: '/',
                    state: { referrer: window.location.href },
                  }}
                />
              )}
            />
          </Switch>
        );
      case ERROR:
        return (
          <Redirect
            to={{
              pathname: '/',
              state: { referrer: window.location.href },
            }}
          />
        );

      default:
        break;
    }

    if (!this.context) {
      return null;
    }

    const Routes = (
      <Switch>
        <Route
          path="/gateway"
          render={() => {
            const { features, permissions } = this.context;

            if (permissions.godMode) {
              return <Redirect to="/networks" />;
            }

            if (permissions.network) {
              return <Redirect to="/home" />;
            }

            if (permissions.podcast) {
              return isS4PodHomeViewForAllEnabled(features) ? (
                <Redirect to="/home" />
              ) : (
                <Redirect to="/catalog" />
              );
            }

            return <Redirect to="/submit" />;
          }}
        />
        <Route path="/episode/:episodeId" component={Episode} />
        <Route path="/podcast/:podcastId" component={Podcast} />
        <Route
          path="/network/:networkId"
          render={({ match }) => (
            <ProtectedNetworkComponent
              permissions={this.context.permissions}
              currentNetworkId={this.props.currentNetworkId}
              handleGodModeAccess={
                /* istanbul ignore next */ () => {
                  this.props.setCurrentNetworkId(match.params.networkId);
                  return <Redirect to="/home" />;
                }
              }
            >
              <Redirect to="/home" />
            </ProtectedNetworkComponent>
          )}
        />
        <Route
          path="/home"
          render={props =>
            isS4PodHomeViewForAllEnabled(this.context.features) ? (
              <Home networkId={this.props.currentNetworkId || null} />
            ) : (
              <ProtectedNetworkComponent
                permissions={this.context.permissions}
                currentNetworkId={this.props.currentNetworkId}
              >
                <ConnectedNetwork {...props}>
                  <ConnectedNetworkLandingPage
                    networkId={this.props.currentNetworkId!}
                    data-qa="network-landing-page"
                  />
                </ConnectedNetwork>
              </ProtectedNetworkComponent>
            )
          }
        />
        <Route
          path="/audience"
          render={props => (
            <ProtectedNetworkComponent
              permissions={this.context.permissions}
              currentNetworkId={this.props.currentNetworkId}
            >
              <ConnectedNetwork {...props}>
                <div data-qa="network-audience">
                  <Type as="h1" variant={Type.heading1}>
                    <FormattedMessage
                      id="network.audience.title"
                      description="Title for the Audience statistics page"
                      defaultMessage="Audience"
                    />
                  </Type>
                  <ConnectedNetworkAudience networkId={this.props.currentNetworkId!} />
                </div>
              </ConnectedNetwork>
            </ProtectedNetworkComponent>
          )}
        />
        <Route
          path="/catalog"
          render={props => (
            <ConnectedNetwork {...props}>
              <div data-qa="network-podcasts-list">
                <Type as="h1" variant={Type.heading1}>
                  Catalog
                </Type>
                <ConnectedPodcastList />
              </div>
            </ConnectedNetwork>
          )}
        />
        <Route
          path="/submit"
          render={({ location, history }) => {
            /**
             * Users coming from the Soundtrap submit flow are redirected to:
             * https://podcasters.spotify.com/submit?st=SOME_HASH
             *
             * If this hash exists we need to move this user into a Soundtrap specific
             * submit flow. The flow is basically the same, but it hides some of
             * the RSS information.
             */

            let searchParams = QueryString.parse(location.search);

            if (searchParams.st) {
              return (
                <AddSoundtrapPodcast
                  soundTrapHash={(searchParams.st as TSFix) as string}
                  me={this.context}
                  loadMe={this.props.loadMe}
                  location={location}
                  history={history}
                />
              );
            }

            return (
              // TODO: ConnectedAddPodcastLandingPage needs to use withRouter to get access to location and history.
              //       instead of manually passing in here.
              // @ts-ignore
              <ConnectedAddPodcastLandingPage location={location} history={history} />
            );
          }}
        />
        <Route path="/networks" component={ConnectedNetworkList} />
        <Route path="/settings" render={() => <Settings me={this.context} />} />

        <Route path="/no-access" component={ErrorPage} />
        <Route
          exact
          path="/faqs"
          component={({ location }) => <Redirect to={{ ...location, pathname: '/faq' }} />}
        />
        <Route path="/faq" component={LoggedInHelpPage} />
        <Route path="*" component={ErrorPage} />
      </Switch>
    );

    return (
      <Layout
        navItems={getNavItems({
          userType: getUserType(this.context),
          isS4PodHomeViewForAllEnabled: isS4PodHomeViewForAllEnabled(this.context.features),
          intl: this.props.intl,
        })}
        banner={this.renderAlertBannerIfNecessary()}
        content={Routes}
      />
    );
  }
}

function getNavItems({
  userType,
  isS4PodHomeViewForAllEnabled,
  intl,
}: {
  userType: UserType;
  isS4PodHomeViewForAllEnabled: boolean;
  intl: IntlShape;
}): { to: string; children: string }[] {
  const messages = defineMessages({
    home: {
      id: 'navigation.home',
      description: 'The Home link in the global navigation bar',
      defaultMessage: 'Home',
    },
    catalog: {
      id: 'navigation.catalog',
      description: 'The Catalog link in the global navigation bar',
      defaultMessage: 'Catalog',
    },
    audience: {
      id: 'navigation.audience',
      description: 'The Audience link in the global navigation bar',
      defaultMessage: 'Audience',
    },
  });
  switch (userType) {
    /* istanbul ignore next */
    case UserType.employee:
    case UserType.network: {
      return [
        { to: `/home`, children: intl.formatMessage(messages.home) },
        { to: `/catalog`, children: intl.formatMessage(messages.catalog) },
        { to: `/audience`, children: intl.formatMessage(messages.audience) },
      ];
    }
    case UserType.podcast: {
      if (isS4PodHomeViewForAllEnabled) {
        return [
          { to: '/home', children: intl.formatMessage(messages.home) },
          { to: `/catalog`, children: intl.formatMessage(messages.catalog) },
        ];
      }

      return [{ to: `/catalog`, children: intl.formatMessage(messages.catalog) }];
    }
    case UserType.empty: {
      return [];
    }
  }
}

const actions = { setCurrentNetworkId, loadMe };

export const ConnectedRoot = connect(
  (state: ReduxState) => ({
    status: selectMeStatus(state),
    currentNetworkId: selectCurrentNetworkId(state),
  }),
  actions,
)(injectIntl(Root as TSFix));
